import { Box, MenuItem, Typography } from '@material-ui/core'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import { centerOfMass } from '@turf/turf'
import L from 'leaflet'
import { debounce } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import { Controller, useFieldArray } from 'react-hook-form'
import { GeoJSON, Marker } from 'react-leaflet'
import AutoSizer from 'react-virtualized-auto-sizer'
import { FixedSizeList as List } from 'react-window'

import Button from '@/components/button'
import InputText from '@/components/input-text'
import { Map } from '@/components/map'
import { CenterToBounds } from '@/components/map/center-to-bounds'
import { MarkerCluster } from '@/components/map/marker-cluster'
import { DEFAULT_COLUMN } from '@/pages/projects-form/constants'
import { useProject } from '@/pages/projects-form/contexts/project-context'
import { GeoJSONObject } from '@/types/geospatial'

import { InputRow } from './input-row'
import { useStyles } from './styles'

export interface GeoJsonHandleProps {
  currentRegionIndex: number
}

export function GeoJsonHandle({ currentRegionIndex }: GeoJsonHandleProps) {
  const classes = useStyles()

  const listRef = useRef<List | null>(null)
  const [currentBounds, setCurrentBounds] = useState<L.LatLngBounds | null>(
    null,
  )

  const {
    form: { control, setValue, watch },
    context: { isEditProjectPage },
  } = useProject()

  const {
    fields: features,
    remove: removeFeature,
    replace: replaceFeatures,
  } = useFieldArray({
    control,
    name: `regions.${currentRegionIndex}.geojson.features`,
    keyName: 'featureId',
  })

  const { append: appendFeatureRemoved } = useFieldArray({
    control,
    name: `regions.${currentRegionIndex}.removed_features`,
    keyName: 'featureId',
  })

  const currentRegion = watch(`regions.${currentRegionIndex}`)

  const initialBounds = L.geoJSON({
    type: 'FeatureCollection',
    features,
  } as GeoJSONObject).getBounds()
  const isNewGeojson = currentRegion.is_new_file
  const currentColumn = currentRegion.col_name

  const allPropertiesFromFirstFeature = Object.entries(
    features.at(0)?.properties,
  )
    .filter(([property]) => property !== DEFAULT_COLUMN && property !== 'id')
    .map(([property, value]) => ({ property, value }))

  const hasValueInAllFeatures = features.every(
    (feature) => feature.properties[DEFAULT_COLUMN],
  )

  function removeFeatureFromGeoJSON(featureIndex: number, id?: string) {
    const isLastFeature = features.length === 1

    if (isEditProjectPage && !currentRegion.is_new_file && id) {
      appendFeatureRemoved({ id })
    }

    if (isLastFeature) {
      return setValue(`regions.${currentRegionIndex}.geojson`, undefined)
    }

    removeFeature(featureIndex)
  }

  function handleRedefine() {
    generateDefaultValues()
  }

  function generateDefaultValues() {
    replaceFeatures(
      features.map((feature, index) => ({
        ...feature,
        properties: {
          ...feature.properties,
          [DEFAULT_COLUMN]: generateDefaultName(index),
        },
      })),
    )
  }

  function generateDefaultName(index: number) {
    return `Talhão ${String(index + 1).padStart(2, '0')}`
  }

  const scrollToIndex = (index: number) => {
    if (listRef.current) {
      listRef.current.scrollToItem(index, 'center')
    }
  }

  useEffect(() => {
    if (isNewGeojson && currentColumn === null && !hasValueInAllFeatures) {
      generateDefaultValues()
      setValue(`regions.${currentRegionIndex}.col_name`, DEFAULT_COLUMN)
    }
  }, [currentColumn, isNewGeojson, hasValueInAllFeatures])

  return (
    <Box
      display='grid'
      gridTemplateColumns='1fr 1fr'
      gridGap='1rem'
      width='100%'
      height='100%'
      minHeight={0}
      minWidth={0}
      paddingBottom='1.6rem'
    >
      <Box
        display='flex'
        minHeight={0}
        flexDirection='column'
        paddingTop='2.6rem'
      >
        {isNewGeojson && (
          <Controller
            control={control}
            name={`regions.${currentRegionIndex}.col_name`}
            defaultValue={DEFAULT_COLUMN}
            render={({ field }) => (
              <Box
                display='flex'
                flexDirection='column'
                marginRight='1.8rem'
                gridGap='1rem'
                alignItems='end'
              >
                <InputText
                  id='column-name-select'
                  {...field}
                  value={
                    field.value === DEFAULT_COLUMN || field.value === null
                      ? ''
                      : String(field.value)
                  }
                  onChange={debounce(field.onChange, 100)}
                  label='Importar nomes da tabela de atributos.'
                  className={classes.columnNameSelect}
                  select
                  SelectProps={{
                    MenuProps: {
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      getContentAnchorEl: null,
                      classes: { paper: classes.menuList },
                      elevation: 3,
                    },
                  }}
                >
                  {allPropertiesFromFirstFeature.map(({ property, value }) => (
                    <MenuItem
                      key={property}
                      className={classes.menuItem}
                      value={property}
                    >
                      {property} - {String(value)}
                    </MenuItem>
                  ))}
                </InputText>

                <Button
                  className={classes.redefineButton}
                  labelOnly
                  onClick={() => {
                    field.onChange(DEFAULT_COLUMN)
                    handleRedefine()
                  }}
                >
                  <AutorenewIcon /> Redefinir nomenclatura
                </Button>
              </Box>
            )}
          />
        )}

        <div className={classes.inputsHeader}>
          <Typography>Nome</Typography>
          <Typography>Área</Typography>
        </div>

        <Box flex={1}>
          <AutoSizer>
            {({ height, width }: { height: number; width: number }) => (
              <List
                className='List'
                ref={listRef}
                height={height}
                width={width}
                itemCount={features.length}
                itemSize={56}
                itemKey={(index) => features[index].featureId}
                itemData={{
                  features,
                  control,
                  currentRegionIndex,
                  currentColumn,
                  removeFeatureFromGeoJSON,
                  setCurrentBounds,
                }}
              >
                {InputRow}
              </List>
            )}
          </AutoSizer>
        </Box>
      </Box>

      <div className={classes.mapContainer}>
        <Typography>Polígono</Typography>

        <Map
          data-testid='map-container'
          bounds={initialBounds}
          whenCreated={(map) => map.invalidateSize()}
          zoomSnap={0.25}
        >
          {features.map((feature) => (
            <GeoJSON
              key={feature.featureId}
              data={feature}
              style={{
                color: 'white',
                fillColor: 'white',
                opacity: 0.4,
                weight: 1,
                interactive: false,
                fillRule: 'nonzero',
              }}
            />
          ))}

          <MarkerCluster showCoverageOnHover={false}>
            {features.map((feature, index) => (
              <Marker
                key={feature.featureId}
                autoPan={false}
                position={
                  centerOfMass(
                    feature,
                  ).geometry.coordinates.reverse() as L.LatLngTuple
                }
                icon={L.divIcon({
                  className: classes.marker,
                  html: renderToStaticMarkup(
                    <label key={feature.featureId} htmlFor={feature.featureId}>
                      {String(feature.properties[currentColumn!]) ?? ''}
                    </label>,
                  ),
                })}
                eventHandlers={{
                  click: () => scrollToIndex(index),
                }}
              />
            ))}
          </MarkerCluster>

          <CenterToBounds bounds={currentBounds} />
        </Map>
      </div>
    </Box>
  )
}
